import AccountBalanceWalletIcon from '@material-design-icons/svg/filled/account_balance_wallet.svg?react';
import CheckIcon from '@material-design-icons/svg/filled/check.svg?react';
import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import RadioButtonCheckedIcon from '@material-design-icons/svg/filled/radio_button_checked.svg?react';
import RadioButtonUncheckedIcon from '@material-design-icons/svg/filled/radio_button_unchecked.svg?react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { isRight } from 'fp-ts/Either';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTRPC } from '../hooks/useTRPC';
import { captureAndReportError } from '../utils/errorReporting';
import { LoadingSpinner } from './ui/LoadingSpinner';

declare global {
  interface Window {
    WALLET_OVERRIDE?: string;
  }
}

const getWalletOverride = (): string | undefined => {
  return typeof window !== 'undefined' && window.WALLET_OVERRIDE
    ? window.WALLET_OVERRIDE
    : undefined;
};

interface FilteredDapp {
  id: string;
  name: string;
  iconUrl: string;
  version: string;
  isImported: boolean;
  androidPackage: string;
}

interface ImportDappDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ImportDappDialog = ({
  isOpen,
  onClose,
}: ImportDappDialogProps) => {
  const { publicKey, connected, disconnect } = useWallet();
  const [walletOverride, setWalletOverride] = useState(getWalletOverride);
  const [step, setStep] = useState<'connect' | 'select'>(
    walletOverride ? 'select' : 'connect',
  );
  const [filteredDapps, setFilteredDapps] = useState<FilteredDapp[]>([]);
  const [selectedDapp, setSelectedDapp] = useState<string | null>(null);
  const [isLoadingDapps, setIsLoadingDapps] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [wasConnectedOnOpen, setWasConnectedOnOpen] = useState(false);
  const trpc = useTRPC();

  useEffect(() => {
    const checkOverride = () => {
      const newOverride = getWalletOverride();
      if (newOverride !== walletOverride) {
        setWalletOverride(newOverride);
        console.log('Wallet override changed:', newOverride);
      }
    };

    const interval = setInterval(checkOverride, 500);

    window.addEventListener('focus', checkOverride);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', checkOverride);
    };
  }, [walletOverride]);

  useEffect(() => {
    if (isOpen) {
      setWasConnectedOnOpen(connected);
      const currentOverride = getWalletOverride();
      setWalletOverride(currentOverride);
      setStep(currentOverride || connected ? 'select' : 'connect');
      setSelectedDapp(null);
    }
  }, [isOpen, connected]);

  useEffect(() => {
    console.log(
      'Effect running - connected:',
      connected,
      'publicKey:',
      publicKey?.toBase58(),
      'step:',
      step,
      'walletOverride:',
      walletOverride,
      'isDisconnecting:',
      isDisconnecting,
    );

    if (!connected && isDisconnecting) {
      setIsDisconnecting(false);
      return;
    }

    if (isDisconnecting) {
      return;
    }

    if (
      (connected && publicKey && step === 'connect') ||
      (walletOverride && step === 'select')
    ) {
      if (!walletOverride) {
        setStep('select');
      }
      setIsLoadingDapps(true);
      const walletAddress = walletOverride || publicKey?.toBase58() || '';
      console.log('Fetching dapps for wallet:', walletAddress);
      trpc.getDappsByWallet
        .query({ walletAddress })
        .then((result) => {
          if ('_tag' in result && isRight(result)) {
            console.log('Got dapps result:', result.right);
            const mappedDapps: FilteredDapp[] = result.right.map((dapp) => ({
              ...dapp,
              isImported: dapp.isImported || false,
            }));
            setFilteredDapps(mappedDapps);
          } else {
            captureAndReportError('Failed to fetch dapps by wallet:', result);
            setFilteredDapps([]);
          }
        })
        .catch((error) => {
          captureAndReportError('Failed to fetch dapps by wallet:', error);
          setFilteredDapps([]);
        })
        .finally(() => {
          setIsLoadingDapps(false);
        });
    }
  }, [connected, publicKey, step, trpc, isDisconnecting, walletOverride]);

  const handleImport = async () => {
    if (!selectedDapp || (!publicKey && !walletOverride)) return;

    const dapp = filteredDapps.find((d) => d.id === selectedDapp);
    if (!dapp) return;

    setIsImporting(true);

    try {
      const walletAddress = walletOverride || publicKey?.toBase58() || '';
      const result = await trpc.importDapp.mutate({
        androidPackage: dapp.androidPackage,
        walletAddress,
      });

      if ('_tag' in result && isRight(result)) {
        toast.success(`${dapp.name} imported successfully!`);

        window.dispatchEvent(new CustomEvent('dappListUpdated'));

        onClose();
      } else {
        const errorMessage =
          '_tag' in result && !isRight(result)
            ? result.left.message
            : 'Failed to import dapp';
        toast.error(errorMessage);
      }
    } catch (error) {
      captureAndReportError('Import error:', error);
      toast.error('An unexpected error occurred while importing the dapp');
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    if (connected && !wasConnectedOnOpen) {
      disconnect();
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[704px] max-w-[90vw] bg-[#151717] rounded-xl shadow-2xl z-50 overflow-hidden border border-[#232626]"
          >
            {step === 'connect' && !walletOverride ? (
              <>
                <div className="p-8">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-6 right-6 p-2 hover:bg-neutral-800 rounded-lg transition-colors"
                  >
                    <CloseIcon className="size-5" style={{ fill: '#ffffff' }} />
                  </button>

                  <div className="flex flex-col items-center justify-center gap-6 py-12">
                    <AccountBalanceWalletIcon
                      className="size-16"
                      style={{ fill: '#ffffff' }}
                    />
                    <div className="text-center">
                      <h3 className="text-2xl font-semibold text-white mb-2">
                        Connect Your Wallet
                      </h3>
                      <p className="text-neutral-400 text-base max-w-sm mx-auto">
                        Connect your Solana wallet to view and import your
                        existing dapps
                      </p>
                    </div>
                    <WalletMultiButton className="!bg-white !text-black hover:!bg-gray-100 !font-semibold !px-8 !py-3 !text-base" />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="p-8">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-6 right-8 p-0 hover:opacity-70 transition-opacity"
                  >
                    <CloseIcon className="size-6" style={{ fill: '#ffffff' }} />
                  </button>

                  <div className="flex flex-col items-center justify-center gap-14 w-[640px] mx-auto">
                    <div className="w-full space-y-8">
                      <div className="text-center">
                        {filteredDapps.length === 0 ? (
                          <>
                            <h1 className="text-[20px] font-semibold text-white leading-7 mb-0">
                              This wallet isn't associated with any dApps
                            </h1>
                            <p className="text-[#99a3a3] text-sm leading-5 font-normal">
                              No dApps found for this wallet.
                            </p>
                          </>
                        ) : (
                          <>
                            <h1 className="text-[20px] font-semibold text-white leading-7 mb-0">
                              This wallet is associated with{' '}
                              {filteredDapps.length}{' '}
                              {filteredDapps.length === 1 ? 'dApp' : 'dApps'}
                            </h1>
                            <p className="text-[#99a3a3] text-sm leading-5 font-normal">
                              Select one to import dApp details.
                            </p>
                          </>
                        )}
                      </div>

                      <div className="w-[335px] mx-auto space-y-1">
                        <p className="text-neutral-400 text-sm leading-5 font-normal">
                          Connected Wallet
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            if (!walletOverride && !wasConnectedOnOpen) {
                              setIsDisconnecting(true);
                              setStep('connect');
                              setFilteredDapps([]);
                              setSelectedDapp(null);
                              disconnect();
                            }
                          }}
                          disabled={!!walletOverride || wasConnectedOnOpen}
                          className="h-10 w-full rounded-xl border border-[#3b4040] flex items-center justify-between px-3 hover:border-[#4b5252] transition-colors cursor-pointer disabled:cursor-default disabled:hover:border-[#3b4040]"
                        >
                          <div className="flex items-center gap-3">
                            <div className="size-5 relative">
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-label="Completed"
                              >
                                <title>Completed</title>
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20Z"
                                  fill="url(#paint0_linear)"
                                />
                                <path
                                  d="M15.81 15.73C16.75 15.73 17.5 14.98 17.5 14.05C17.5 13.11 16.75 12.36 15.81 12.36C14.88 12.36 14.13 13.11 14.13 14.05C14.13 14.98 14.88 15.73 15.81 15.73Z"
                                  fill="white"
                                />
                                <path
                                  d="M4.29 15.73C5.22 15.73 5.97 14.98 5.97 14.05C5.97 13.11 5.22 12.36 4.29 12.36C3.35 12.36 2.6 13.11 2.6 14.05C2.6 14.98 3.35 15.73 4.29 15.73Z"
                                  fill="white"
                                />
                                <path
                                  d="M10.05 10.35C11.91 10.35 13.42 8.84 13.42 6.98C13.42 5.12 11.91 3.61 10.05 3.61C8.19 3.61 6.68 5.12 6.68 6.98C6.68 8.84 8.19 10.35 10.05 10.35Z"
                                  fill="white"
                                />
                                <defs>
                                  <linearGradient
                                    id="paint0_linear"
                                    x1="10"
                                    y1="0"
                                    x2="10"
                                    y2="20"
                                    gradientUnits="userSpaceOnUse"
                                  >
                                    <stop stopColor="#534BB1" />
                                    <stop offset="1" stopColor="#551BF9" />
                                  </linearGradient>
                                </defs>
                              </svg>
                            </div>
                            <span className="text-white font-bold text-sm">
                              {walletOverride ? (
                                <>
                                  {walletOverride.slice(0, 5).toUpperCase()}...
                                  {walletOverride.slice(-5)}
                                  <span className="text-xs text-neutral-400 ml-1">
                                    (Override)
                                  </span>
                                </>
                              ) : (
                                <>
                                  {publicKey
                                    ?.toBase58()
                                    .slice(0, 5)
                                    .toUpperCase()}
                                  ...{publicKey?.toBase58().slice(-5)}
                                </>
                              )}
                            </span>
                          </div>
                          <span className="text-xs text-neutral-400">
                            {walletOverride
                              ? 'Override'
                              : wasConnectedOnOpen
                                ? 'Connected'
                                : 'Disconnect'}
                          </span>
                        </button>
                      </div>

                      <div className="w-[335px] mx-auto space-y-2">
                        <p className="text-neutral-400 text-sm leading-5 font-normal mb-1">
                          Associated dApps
                        </p>
                        {isLoadingDapps ? (
                          <div className="text-center py-8">
                            <p className="text-neutral-500 text-sm">
                              Loading dApps…
                            </p>
                          </div>
                        ) : filteredDapps.length === 0 ? (
                          <div className="text-center py-8">
                            <p className="text-neutral-500 text-sm">
                              No dApps found for this wallet
                            </p>
                          </div>
                        ) : (
                          filteredDapps.map((dapp) => (
                            <div
                              key={dapp.id}
                              onClick={() =>
                                !dapp.isImported && setSelectedDapp(dapp.id)
                              }
                              className={`h-10 rounded-xl flex items-center justify-between px-4 cursor-pointer transition-all ${
                                selectedDapp === dapp.id
                                  ? 'bg-white'
                                  : dapp.isImported
                                    ? 'bg-[#151717] cursor-not-allowed'
                                    : 'bg-[#232626] hover:bg-[#2a2d2d]'
                              }`}
                            >
                              <div className="flex items-center gap-4">
                                {dapp.isImported ? (
                                  <CheckIcon
                                    className="size-4"
                                    style={{
                                      fill:
                                        selectedDapp === dapp.id
                                          ? '#000000'
                                          : '#ffffff',
                                    }}
                                  />
                                ) : selectedDapp === dapp.id ? (
                                  <RadioButtonCheckedIcon className="size-4 text-black" />
                                ) : (
                                  <RadioButtonUncheckedIcon className="size-4 text-white" />
                                )}

                                <div className="flex items-center gap-[7px]">
                                  <div className="size-5 rounded-md flex items-center justify-center overflow-hidden bg-neutral-700">
                                    <img
                                      src={dapp.iconUrl}
                                      alt={dapp.name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        const target =
                                          e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        const avatarParent =
                                          target.parentElement;
                                        if (!avatarParent) {
                                          return;
                                        }
                                        avatarParent.innerHTML = `<div class="w-5 h-5 bg-gradient-to-br from-neutral-600 to-neutral-700 rounded-md flex items-center justify-center text-xs font-bold text-white">${dapp.name.charAt(0).toUpperCase()}</div>`;
                                      }}
                                    />
                                  </div>
                                  <span
                                    className={`font-semibold text-sm ${
                                      selectedDapp === dapp.id
                                        ? 'text-black'
                                        : dapp.isImported
                                          ? 'text-[#96a3a3]'
                                          : 'text-white'
                                    }`}
                                  >
                                    {dapp.name}
                                  </span>
                                </div>
                              </div>

                              <span
                                className={`text-xs ${
                                  selectedDapp === dapp.id
                                    ? 'text-[#4b5252]'
                                    : 'text-[#96a3a3]'
                                }`}
                              >
                                {dapp.isImported ? 'Imported' : dapp.version}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="w-[335px] mx-auto">
                      <button
                        type="button"
                        onClick={handleImport}
                        disabled={
                          !selectedDapp ||
                          filteredDapps.find((d) => d.id === selectedDapp)
                            ?.isImported ||
                          isImporting
                        }
                        className={`w-full h-10 rounded-xl font-bold text-sm transition-all flex items-center justify-center ${
                          selectedDapp &&
                          !filteredDapps.find((d) => d.id === selectedDapp)
                            ?.isImported &&
                          !isImporting
                            ? 'bg-gradient-to-b from-[#2ea597] from-[4.4%] via-[#2b8d8f] via-[41.087%] to-[#2b888f] text-white hover:opacity-90'
                            : 'bg-[#232626] text-[#96a3a3] cursor-not-allowed'
                        }`}
                      >
                        {isImporting ? (
                          <div className="flex items-center gap-2">
                            <LoadingSpinner size="sm" color="currentColor" />
                            <span>Importing…</span>
                          </div>
                        ) : (
                          'Import dApp'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
